import { Injectable } from '@angular/core';
import { QueryEntity, QueryConfig, Order } from '@datorama/akita';
import { LottoStore, LottoState } from './lotto.store';
import { Observable, combineLatest } from 'rxjs';
import { LottoItem } from './models/lotto-item.model';
import { map } from 'rxjs/operators';
import { BalanceQuery } from '../../account/balance/balance.query';
import { config } from '../../shared/config';
import { LoyaltyLimitType } from './models/loyalty-limit-type';

@Injectable({ providedIn: 'root' })
@QueryConfig({ sortBy: 'EndDate', sortByOrder: Order.ASC })
export class LottoQuery extends QueryEntity<LottoState> {
  constructor(protected store: LottoStore, private balanceQuery: BalanceQuery) {
    super(store);
  }

  /** All lottos. */
  selectLottos(): Observable<LottoItem[]> {
    return this.selectAll();
  }

  /** A specific lotto by ID. */
  selectLotto(id: number): Observable<LottoItem | undefined> {
    return this.selectEntity(id);
  }

  getPrice(id: number): number | undefined {
    return this.getEntity(id)?.PurchasePrice;
  }

  /** Favourite lottos. */
  selectFavourites(): Observable<LottoItem[]> {
    return this.selectAll({ filterBy: (item) => item.Favourite === 1 });
  }
  selectEntriesLimit(lottoId: number, memberId?: string) {
    return this._maxEntries(lottoId, memberId).pipe(map((x) => x?.limit || 0));
  }
  selectEntriesLimitMessage(lottoId: number, memberId?: string) {
    return this._maxEntries(lottoId, memberId).pipe(
      map((x) => {
        return x?.message || '';
      })
    );
  }
  private _maxEntries(lottoId: number, memberId?: string) {
    return combineLatest([
      this.selectLotto(lottoId),
      this.balanceQuery.selectPurse(config.purseConfig.virtualLoyalty, memberId),
    ]).pipe(
      map(([lotto, balance]) => {
        if (lotto == undefined || balance == undefined) {
          return;
        }
        let limit = lotto.MaxTotalTicketLimit;
        let message = '';

        if (lotto.LimitType !== LoyaltyLimitType.NoLimit) {
          switch (lotto.LimitType) {
            case LoyaltyLimitType.LimitByAccount:
              limit = lotto.LimitCount - lotto.NumberOfTicketsPurchasedByAccount;
              message = 'error.lotto-accountLimitReached';
              break;
            case LoyaltyLimitType.LimitByMember:
              limit = lotto.LimitCount - lotto.NumberOfTicketsPurchased;
              message = 'error.lotto-memberLimitReached';
              break;
            default:
              limit = lotto.LimitCount;
              break;
          }

          if (limit <= 0) {
            limit = 0;
            message = 'error.loyalty-memberLimitReached';
            return { limit, message };
          }
        }

        if (!lotto.UnlimitedLimit && lotto.AvailableCount < limit) {
          if (lotto.AvailableCount > 0) {
            limit = lotto.AvailableCount;
          } else {
            limit = 0;
            message = 'error.marketplace-notEnoughStock';
            return { limit, message };
          }
        }

        let quantityLimitFromFunds: number;
        if (!!balance) {
          const purchasePrice = lotto.PurchasePrice;

          if( purchasePrice === 0 ){
            
            const availableCount = lotto.AvailableCount > 0 ? lotto.AvailableCount : 100;
            quantityLimitFromFunds = availableCount;
          }else{
            quantityLimitFromFunds = Math.floor(balance / lotto.PurchasePrice);
          }
        } else {
          quantityLimitFromFunds = 10;
        }

        if (quantityLimitFromFunds < limit || (lotto.UnlimitedLimit && limit == 0)) {
          limit = quantityLimitFromFunds;
          if (quantityLimitFromFunds === 0) {
            message = 'error.user-insufficientFunds';
          }
        }

        return { limit, message };
      })
    );
  }
}
